import { UnstyledButton } from '@mantine/core';
import type { ConversationTurn, TtsAudio } from '@nelnela/contracts';
import { ERROR_RULE } from '../../lib/error-tint';
import type { BubbleRun } from '../../lib/chat';

/**
 * One chat turn (design `8e`).
 *
 * **A new run-based renderer, not `Transcript`.** `Transcript`'s value is marks from `line.items`,
 * the EN reveal pill and the time gutter, and a chat turn has none of the three — an adapter would
 * pass `items: undefined`, `translation: null`, `gutter: 'none'` and inherit a pile of machinery
 * for nothing. `components/reader/CultureNote.tsx` is the precedent for doing it this way and for
 * the same reason. What a bubble DOES need from the reading surface is the shared tokenizer, the
 * `.nn-w` hit box, `--nn-transcript-size` and the gloss sheet, because `8e` promises *any word
 * tappable → gloss*, and it has all four.
 *
 * **It takes the whole `ConversationTurn`, `audio` included** (M6D-HANDOFF § "Three things to ask
 * M6c for now"). Text chat has no audio and `audio` is null on every turn this milestone writes,
 * so nothing is drawn for it; when M6d fills the slot it wires `onPlayAudio` and adds no field and
 * changes no signature. A component typed `{ text: string }` is the retrofit that ask exists to
 * prevent.
 *
 * **The third mark, and how the two-mark rule survives it.** `reading-surface-spec.md` §2 allows
 * exactly two marks (dotted = weak word, solid = chunk) and the brief adds "no traffic-light
 * colors on text, ever". A correction underline in the error tint is a third. It is allowed here
 * for the same reason it is allowed in the journal — this is the learner's OWN text — and it is
 * alone, because a bubble is passed no `marks` at all and so draws no weak-word dots to compete
 * with it. One mark at a time, error wins: on your own sentence what is *wrong* matters more than
 * what is *shaky*.
 */

export interface BubbleProps {
  turn: ConversationTurn;
  /** the turn's text cut into runs — `bubbleRuns(turn.text, turnPlacements(turn, acted))` */
  runs: readonly BubbleRun[];
  /** any word → the gloss sheet. Offsets are into `turn.text`. */
  onWord: (
    turn: ConversationTurn,
    word: { surface: string; charStart: number; charEnd: number },
  ) => void;
  /** a tap on an underlined word → the F12 correction card (`8e`'s own caption) */
  onCorrection: (turn: ConversationTurn, correctionId: string) => void;
  /** [M6d] spoken dialogue's play control. Absent in M6c, and `turn.audio` is null anyway. */
  onPlayAudio?: (turn: ConversationTurn, audio: TtsAudio) => void;
}

export function Bubble({ turn, runs, onWord, onCorrection, onPlayAudio }: BubbleProps) {
  const mine = turn.speaker === 'learner';
  return (
    <div style={mine ? learnerBubble : appBubble}>
      {/* `nn-reading` carries the reading-surface rules the spec §3 mandates — no OS selection, no
          callout, `touch-action: pan-y pinch-zoom` — and the size custom property the learner's
          16-18 px setting arrives in. The screen sets `--nn-transcript-size` once on the scroll
          box; every bubble inherits it, so the two speakers can never disagree about type.
          ⟂ `8e` draws 14.5 px; it loses to reading-surface-spec §1 and to the setting, exactly as
          `8d`'s 15 px lost to them in the journal. */}
      <div className="nn-reading" lang="ka" style={bubbleText}>
        {runs.map((r, i) => {
          if (!r.word) {
            // Whitespace and punctuation: a bare text node unless a correction covers it, in which
            // case it carries the underline so the mark is continuous across a two-word span.
            return r.correctionId ? (
              <span key={i} style={r.stale ? staleUnderline : underline}>
                {r.text}
              </span>
            ) : (
              <span key={i}>{r.text}</span>
            );
          }
          const word = r.word;
          const marked = r.correctionId !== undefined;
          // A tap on a corrected word opens the card, not the gloss. `8e`'s caption says so
          // ("tap → the F12 card"), and it is the reading surface's own precedent — spec §3's
          // "tap inside a chunk → chunk card first": the more specific mark wins. It is also the
          // more useful answer, because glossing the word you got wrong tells you the meaning of
          // something you did not write.
          return (
            <span
              key={i}
              className="nn-w"
              role="button"
              tabIndex={-1}
              style={marked ? (r.stale ? staleUnderline : underline) : undefined}
              onClick={() => {
                if (marked && !r.stale) {
                  onCorrection(turn, r.correctionId!);
                  return;
                }
                onWord(turn, word);
              }}
            >
              {r.text}
            </span>
          );
        })}
      </div>
      {/* [M6d] The audio slot. Nothing is drawn while `audio` is null, which is every turn in
          M6c — text chat has no TTS and that is M6d's cost, not this milestone's. */}
      {turn.audio && onPlayAudio && (
        <UnstyledButton
          aria-label="Play"
          onClick={() => onPlayAudio(turn, turn.audio!)}
          style={{ marginTop: 5, fontSize: 11, color: 'var(--nn-muted)' }}
        >
          ▶ play
        </UnstyledButton>
      )}
    </div>
  );
}

/**
 * The app's half of an exchange, while it is being written.
 *
 * `.nn-shimmer` is a global class already — `READER_CSS` is concatenated into `App.tsx`'s single
 * `<style>` element — so this needs no new stylesheet and no wiring. A spinner is forbidden by the
 * rule M6b applied to an unwarmed piece: a wheel says "something is stuck", three bars in the
 * shape of a message say "a message is coming", which is the truth.
 *
 * Two bars rather than three: the prompt asks for one or two short sentences, and a placeholder
 * bigger than the thing it stands in for makes the real reply look like a disappointment.
 */
export function PendingBubble() {
  return (
    <div style={{ ...appBubble, minWidth: 132 }} aria-label="writing…" role="status">
      <span className="nn-shimmer" style={{ width: '100%' }} />
      <span className="nn-shimmer" style={{ width: '62%', marginTop: 6 }} />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// `8e`'s two shapes
// ---------------------------------------------------------------------------------------------

const bubbleBase: React.CSSProperties = {
  maxWidth: '78%',
  padding: '9px 13px',
  // A bubble is not a fixed-height box: a long Georgian sentence has to be allowed to wrap, and
  // `min-width: 0` is what stops a flex column from refusing to shrink it below its longest word.
  minWidth: 0,
  overflowWrap: 'break-word',
};

/** `#181d24` in `8e` is `--nn-panel` exactly, so the light scheme derives for free (README §5). */
const appBubble: React.CSSProperties = {
  ...bubbleBase,
  alignSelf: 'flex-start',
  background: 'var(--nn-panel)',
  border: '1px solid var(--nn-hairline)',
  borderRadius: '12px 12px 12px 4px',
};

/**
 * The learner's, ember-tinted and right-aligned.
 *
 * `color-mix(in oklab, …)` is `8e`'s own function and its own 16 %: oklab keeps the tint
 * perceptually even against both grounds, which is why the design chose it over an rgba literal
 * that would have to be re-picked for the light scheme.
 */
const learnerBubble: React.CSSProperties = {
  ...bubbleBase,
  alignSelf: 'flex-end',
  background: 'color-mix(in oklab, var(--nn-accent) 16%, transparent)',
  border: '1px solid color-mix(in srgb, var(--nn-accent) 30%, transparent)',
  borderRadius: '12px 12px 4px 12px',
};

const bubbleText: React.CSSProperties = {
  fontFamily: "'Noto Sans Georgian', sans-serif",
  fontSize: 'var(--nn-transcript-size)',
  lineHeight: 1.6,
  color: 'var(--nn-text-strong)',
};

/**
 * The correction underline — `8d`'s values, and the same object the journal's mirror paints.
 *
 * Inline rather than in a stylesheet for the reason `Journal.tsx` states about its own copy: if
 * the app-wide `<style>` were ever not concatenated, an underline hung off a class would vanish
 * silently and the chat would look like it simply found no mistakes. The colour is `ERROR_RULE` —
 * the coral-red error tint, never the accent, which on this screen already means "this bubble is
 * yours" and would be a small cruelty to paint a mistake with (reading-surface-spec §7).
 */
const underline: React.CSSProperties = {
  textDecorationLine: 'underline',
  textDecorationStyle: 'solid',
  textDecorationColor: ERROR_RULE,
  textDecorationThickness: '1.5px',
  textUnderlineOffset: '.2em',
  textDecorationSkipInk: 'none',
};

/** Dealt with: still visible, no longer live. `8d`: "stale corrections fade". */
const staleUnderline: React.CSSProperties = { ...underline, opacity: 0.45 };
